import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom fuel station icon
const fuelIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// User location icon
const userIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to recenter map
function RecenterMap({ center, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
}

export function FuelStationsMap({ 
  stations = [], 
  userLocation = null, 
  radius = 5000,
  selectedStation = null,
  onStationSelect = () => {},
  className = '' 
}) {
  // Calculate center and zoom based on props
  const mapCenter = userLocation 
    ? [userLocation.lat, userLocation.lon] 
    : [46.8, 8.2]; // Default: Switzerland
  
  const mapZoom = (() => {
    if (!userLocation) return 13;
    if (radius <= 2000) return 15;
    if (radius <= 5000) return 13;
    if (radius <= 10000) return 12;
    return 11;
  })();

  const formatDistance = (meters) => {
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  };

  return (
    <div className={`relative ${className}`} style={{ height: '300px', width: '100%' }}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%', borderRadius: '0' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <RecenterMap center={mapCenter} zoom={mapZoom} />
        
        {/* Search radius circle */}
        {userLocation && (
          <Circle
            center={[userLocation.lat, userLocation.lon]}
            radius={radius}
            pathOptions={{
              color: '#FF5F1F',
              fillColor: '#FF5F1F',
              fillOpacity: 0.1,
              weight: 2,
              dashArray: '5, 5'
            }}
          />
        )}
        
        {/* User location marker */}
        {userLocation && (
          <Marker position={[userLocation.lat, userLocation.lon]} icon={userIcon}>
            <Popup>
              <div className="text-center">
                <strong>La tua posizione</strong>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Fuel station markers */}
        {stations.map((station) => (
          <Marker 
            key={station.id} 
            position={[station.lat, station.lon]} 
            icon={fuelIcon}
            eventHandlers={{
              click: () => onStationSelect(station)
            }}
          >
            <Popup>
              <div className="min-w-[200px]">
                <h3 className="font-bold text-sm mb-1">{station.name}</h3>
                {station.brand && (
                  <p className="text-xs text-gray-600 mb-1">{station.brand}</p>
                )}
                {station.address && (
                  <p className="text-xs text-gray-500 mb-2">{station.address}</p>
                )}
                {station.fuel_types && station.fuel_types.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {station.fuel_types.map((type, idx) => (
                      <span key={idx} className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                        {type}
                      </span>
                    ))}
                  </div>
                )}
                {station.opening_hours && (
                  <p className="text-xs text-gray-500 mb-1">
                    🕐 {station.opening_hours}
                  </p>
                )}
                {station.distance_m && (
                  <p className="text-xs font-medium text-orange-600">
                    📍 {formatDistance(station.distance_m)}
                  </p>
                )}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${station.lat},${station.lon}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 block text-center text-xs bg-blue-500 text-white py-1 px-2 rounded hover:bg-blue-600"
                >
                  Naviga con Google Maps
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

export default FuelStationsMap;
